import { useEffect, useRef, useState } from "react";

const fadeInClass = "opacity-0 translate-y-6";
const fadeInVisible = "opacity-100 translate-y-0 transition-all duration-700 ease-out";

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

const pillLabel = "inline-block px-4 py-1.5 bg-warm-100 text-warm-600 text-xs font-semibold tracking-widest rounded-full";

export default function ContactPage() {
  const hero = useInView<HTMLDivElement>();
  const targetItems = useInView<HTMLDivElement>();
  const platforms = useInView<HTMLDivElement>();
  const conditions = useInView<HTMLDivElement>();
  const achievements = useInView<HTMLDivElement>();
  const policy = useInView<HTMLDivElement>();
  const contact = useInView<HTMLDivElement>();

  const items = [
    {
      title: "Apple周辺機器",
      desc: "iPhoneケース、iPadケース、充電器、MagSafeアクセサリーなど",
      img: "https://readdy.ai/api/search-image?query=Premium%20Apple%20accessories%20including%20iPhone%20case%20iPad%20case%20MagSafe%20charger%20arranged%20on%20a%20minimal%20white%20desk%20with%20soft%20natural%20lighting%20and%20warm%20beige%20accents%20high%20quality%20product%20photography%20style%20clean%20aesthetic%20professional&width=300&height=200&seq=1&orientation=landscape",
    },
    {
      title: "ガジェット全般",
      desc: "ワイヤレスイヤホン、モバイルバッテリー、スマートウォッチなど",
      img: "https://readdy.ai/api/search-image?query=Collection%20of%20modern%20tech%20gadgets%20including%20wireless%20earbuds%20smartwatch%20power%20bank%20arranged%20on%20white%20surface%20minimal%20aesthetic%20soft%20warm%20lighting%20professional%20product%20photography%20style&width=300&height=200&seq=2&orientation=landscape",
    },
    {
      title: "デスクアイテム",
      desc: "モニターアーム、キーボード、マウス、デスクライトなど",
      img: "https://readdy.ai/api/search-image?query=Modern%20desk%20setup%20with%20monitor%20arm%20mechanical%20keyboard%20wireless%20mouse%20desk%20lamp%20on%20clean%20white%20desk%20minimal%20aesthetic%20soft%20warm%20lighting%20professional%20workspace%20photography%20style&width=300&height=200&seq=3&orientation=landscape",
    },
    {
      title: "スマートホーム",
      desc: "スマートスピーカー、スマート照明、ホームカメラなど",
      img: "https://readdy.ai/api/search-image?query=Smart%20home%20devices%20including%20smart%20speaker%20smart%20lighting%20security%20camera%20arranged%20in%20modern%20living%20room%20white%20minimal%20background%20warm%20lighting%20professional%20product%20photography%20style&width=300&height=200&seq=4&orientation=landscape",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-warm-900 font-sans">
      {/* Hero */}
      <section className="pt-16 pb-16 md:pt-24 md:pb-24 px-6 md:px-8 max-w-7xl mx-auto">
        <div
          ref={hero.ref}
          className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
            hero.inView ? fadeInVisible : fadeInClass
          }`}
        >
          <div className="w-full lg:w-[45%] text-center lg:text-left">
            <span className={pillLabel}>FOR CORPORATE</span>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
              企業様向け
              <br />
              製品レビュー
            </h1>
            <p className="mt-6 text-warm-500 text-base md:text-lg leading-relaxed max-w-md mx-auto lg:mx-0">
              ショート動画とブログ記事を中心に、累計300以上の製品レビューを行ってきました。ガジェットやデスクアイテムの魅力を、ありのままにお伝えします。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="#contact"
                className="px-8 py-3.5 bg-warm-900 text-white text-sm font-semibold rounded-full hover:bg-warm-700 transition-colors whitespace-nowrap"
              >
                お問い合わせはこちら
              </a>
            </div>
            <div className="mt-10 flex items-center gap-5 justify-center lg:justify-start">
              <a href="https://www.instagram.com/__number84/" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-warm-200 text-warm-500 hover:text-warm-900 hover:border-warm-400 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-lg" />
              </a>
              <a href="https://x.com/Number84M" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-warm-200 text-warm-500 hover:text-warm-900 hover:border-warm-400 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-lg" />
              </a>
              <a href="https://www.tiktok.com/@number84m" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-warm-200 text-warm-500 hover:text-warm-900 hover:border-warm-400 transition-colors cursor-pointer">
                <i className="ri-tiktok-line text-lg" />
              </a>
              <a href="https://www.youtube.com/@Number84M" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-warm-200 text-warm-500 hover:text-warm-900 hover:border-warm-400 transition-colors cursor-pointer">
                <i className="ri-youtube-line text-lg" />
              </a>
              <a href="https://number84log.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-warm-200 text-warm-500 hover:text-warm-900 hover:border-warm-400 transition-colors cursor-pointer">
                <i className="ri-global-line text-lg" />
              </a>
            </div>
          </div>
          <div className="w-full lg:w-[55%]">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/9ec23c94-2058-45c5-b820-01c0e2a11668/91693bb9-3426-4d66-b5e1-b95f73edb99c_-2.jpg.webp?v=e821937a026401a5696b70298a33176b"
                alt="デスク上のガジェット"
                className="w-full h-auto object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 対象アイテム */}
      <section id="target-items" className="py-16 md:py-24 px-6 md:px-8 bg-warm-50">
        <div className="max-w-7xl mx-auto">
          <div
            ref={targetItems.ref}
            className={`text-center mb-12 md:mb-16 ${targetItems.inView ? fadeInVisible : fadeInClass}`}
          >
            <span className={pillLabel}>REVIEW TARGETS</span>
            <div className="mt-4 text-3xl md:text-4xl font-bold">レビュー対象製品</div>
            <p className="mt-4 text-warm-500 text-base max-w-xl mx-auto">
              ガジェットやライフスタイル製品のレビューを幅広く承っております
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((item, i) => (
              <div
                key={item.title}
                className={`bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                  targetItems.inView ? fadeInVisible : fadeInClass
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[3/2] w-full overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="font-bold text-base mb-1.5">{item.title}</div>
                  <p className="text-warm-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* レビュー媒体 */}
      <section className="py-16 md:py-24 px-6 md:px-8 max-w-7xl mx-auto">
        <div
          ref={platforms.ref}
          className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
            platforms.inView ? fadeInVisible : fadeInClass
          }`}
        >
          <div className="w-full lg:w-[40%] text-center lg:text-left">
            <span className={pillLabel}>PLATFORMS</span>
            <div className="mt-4 text-3xl md:text-4xl font-bold">配信媒体</div>
            <p className="mt-4 text-warm-500 text-base leading-relaxed">
              ショート動画とブログ記事で多角的にレビューを展開。各媒体の特性を活かし、製品の魅力を最大限に伝えます。
            </p>
          </div>
          <div className="w-full lg:w-[60%] grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="bg-white border border-warm-200 rounded-2xl p-6 text-center transition-all duration-300 hover:border-warm-400">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-instagram-line text-xl text-warm-700" />
                </div>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-youtube-line text-xl text-warm-700" />
                </div>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-tiktok-line text-xl text-warm-700" />
                </div>
              </div>
              <div className="font-semibold text-sm">ショート動画</div>
              <p className="mt-2 text-warm-500 text-xs leading-relaxed">Instagram Reels / YouTube Shorts / TikTok</p>
            </div>
            <div className="bg-white border border-warm-200 rounded-2xl p-6 text-center transition-all duration-300 hover:border-warm-400">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-article-line text-xl text-warm-700" />
              </div>
              <div className="font-semibold text-sm">ブログ記事</div>
              <p className="mt-2 text-warm-500 text-xs leading-relaxed">詳細なスペック検証や長期使用レポート</p>
            </div>
            <div className="bg-white border border-warm-200 rounded-2xl p-6 text-center transition-all duration-300 hover:border-warm-400">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-bard-line text-xl text-warm-700" />
              </div>
              <div className="font-semibold text-sm">マルチ展開</div>
              <p className="mt-2 text-warm-500 text-xs leading-relaxed">動画と記事の組み合わせで相乗効果</p>
            </div>
          </div>
        </div>
      </section>

      {/* レビュー条件 */}
      <section id="conditions" className="py-16 md:py-24 px-6 md:px-8 bg-warm-50">
        <div className="max-w-4xl mx-auto">
          <div
            ref={conditions.ref}
            className={`text-center mb-12 md:mb-16 ${conditions.inView ? fadeInVisible : fadeInClass}`}
          >
            <span className={pillLabel}>REVIEW CONDITIONS</span>
            <div className="mt-4 text-3xl md:text-4xl font-bold">ご依頼条件</div>
            <p className="mt-4 text-warm-500 text-base">
              より良いレビューをお届けするため、以下の条件を設けさせていただいております
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div
              className={`bg-white rounded-2xl p-8 text-center ${conditions.inView ? fadeInVisible : fadeInClass}`}
              style={{ transitionDelay: "100ms" }}
            >
              <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-box-3-line text-xl text-warm-700" />
                </div>
              </div>
              <div className="text-lg font-bold mb-2">製品提供</div>
              <p className="text-warm-500 text-sm leading-relaxed">
                実際に使用してレビューを作成するため、製品の提供をお願いしております。使用期間は製品により異なります。
              </p>
            </div>
            <div
              className={`bg-white rounded-2xl p-8 text-center ${conditions.inView ? fadeInVisible : fadeInClass}`}
              style={{ transitionDelay: "200ms" }}
            >
              <div className="w-12 h-12 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-coins-line text-xl text-warm-700" />
                </div>
              </div>
              <div className="text-lg font-bold mb-2">PR料</div>
              <p className="text-warm-500 text-sm leading-relaxed">
                製品提供に加え、執筆・撮影・編集の対価としてPR料をいただいております。詳細はお問い合わせください。
              </p>
            </div>
          </div>
          <p
            className={`mt-8 text-center text-warm-400 text-sm italic ${
              conditions.inView ? fadeInVisible : fadeInClass
            }`}
            style={{ transitionDelay: "300ms" }}
          >
            ※ 基本的に製品提供とPR料の両方が必要となります。
          </p>
        </div>
      </section>

      {/* レビュー実績 + イベント */}
      <section id="achievements" className="py-16 md:py-24 px-6 md:px-8 max-w-7xl mx-auto">
        <div
          ref={achievements.ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
            achievements.inView ? fadeInVisible : fadeInClass
          }`}
        >
          <div className="text-center lg:text-left">
            <span className={pillLabel}>ACHIEVEMENTS</span>
            <div className="mt-6">
              <span className="text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter">300+</span>
              <p className="mt-2 text-lg font-semibold text-warm-600">製品レビュー実績</p>
            </div>
            <p className="mt-6 text-warm-500 text-base leading-relaxed max-w-md mx-auto lg:mx-0">
              スマートフォン周辺機器からデスク環境、スマートホームまで、多様なカテゴリーの製品レビューを積み重ねてきました。
            </p>
          </div>
          <div>
            <div className="text-xl font-bold mb-5 text-center lg:text-left">イベント参加歴</div>
            <div className="space-y-4">
              <div className="bg-white border border-warm-200 rounded-xl p-5 flex items-center gap-4 transition-all duration-300 hover:border-warm-400">
                <div className="w-10 h-10 bg-warm-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-lg text-warm-700" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-sm">Apple様 招待制イベント</p>
                  <p className="text-warm-500 text-xs mt-0.5">新製品発表会や体験イベントへの参加実績あり</p>
                </div>
              </div>
              <div className="bg-white border border-warm-200 rounded-xl p-5 flex items-center gap-4 transition-all duration-300 hover:border-warm-400">
                <div className="w-10 h-10 bg-warm-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-lg text-warm-700" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-sm">BenQ様 招待制イベント</p>
                  <p className="text-warm-500 text-xs mt-0.5">ディスプレイ・モニター関連の展示会へ参加</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* レビューポリシー */}
      <section className="py-16 md:py-24 px-6 md:px-8 bg-warm-100">
        <div
          ref={policy.ref}
          className={`max-w-3xl mx-auto text-center ${policy.inView ? fadeInVisible : fadeInClass}`}
        >
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
            <i className="ri-shield-check-line text-3xl text-warm-700" />
          </div>
          <div className="text-2xl md:text-3xl font-bold mb-4">レビューポリシー</div>
          <p className="text-warm-700 text-base md:text-lg leading-[1.8] font-medium">
            製品レビューは筆者が感じたありのままの内容となり、
            <br className="hidden md:block" />
            メーカーへの忖度はできません。
          </p>
          <p className="mt-4 text-warm-500 text-sm leading-relaxed">
            良い点も改善点も正直にお伝えすることで、読者の購買判断に役立つ情報を提供します。
            そのため、ネガティブな内容を含む場合でも事前に内容の修正依頼にはお応えできません。
          </p>
        </div>
      </section>

      {/* お問い合わせ */}
      <section id="contact" className="py-16 md:py-24 px-6 md:px-8 max-w-7xl mx-auto">
        <div
          ref={contact.ref}
          className={`grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 ${contact.inView ? fadeInVisible : fadeInClass}`}
        >
          <div className="lg:col-span-2 text-center lg:text-left">
            <span className={pillLabel}>CONTACT</span>
            <div className="mt-4 text-3xl md:text-4xl font-bold">お問い合わせ</div>
            <p className="mt-4 text-warm-500 text-base leading-relaxed">
              製品レビューのご依頼は下記フォームよりお気軽にご連絡ください。内容を確認の上、数日以内にご返信いたします。
            </p>
            <div className="mt-8 space-y-3 text-sm text-warm-600">
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-warm-400" />
                </div>
                <span>返信目安：2〜3営業日</span>
              </div>
              <div className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-warm-400" />
                </div>
                <span>メールにてご連絡いたします</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div id="wp-contact-form-slot"></div>
          </div>
        </div>
      </section>
    </div>
  );
}